import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiBook, FiFileText, FiVideo, FiHelpCircle, FiChevronDown, FiChevronUp, FiGrid, FiList, FiFile, FiClock } from 'react-icons/fi';
import Toast from './Toast';

import { API_URL } from '../config';

function AdminTraining() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [expandedTrainings, setExpandedTrainings] = useState(new Set());
  const [trainingFormData, setTrainingFormData] = useState({
    title: '',
    description: '',
    order: 0,
  });
  const [moduleFormData, setModuleFormData] = useState({
    title: '',
    description: '',
    type: 'text',
    content: '',
    video_url: '',
    file_url: '',
    video_source: 'embed', // 'embed' or 'upload'
    order: 0,
  });
  const [videoFile, setVideoFile] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [quizFormData, setQuizFormData] = useState({
    title: '',
    description: '',
    passing_score: 70,
  });
  const [questionFormData, setQuestionFormData] = useState({
    question: '',
    question_type: 'multiple_choice',
    options: ['', '', '', ''],
    correct_answer: 0,
    order: 0,
  });
  const [pendingQuizSubmissions, setPendingQuizSubmissions] = useState([]);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [gradeBundle, setGradeBundle] = useState(null);
  const [writeInGrades, setWriteInGrades] = useState({});
  const [gradingSubmitting, setGradingSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    loadTrainings();
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      setPendingQuizSubmissions([]);
      return;
    }
    axios
      .get(`${API_URL}/quizzes/submissions/pending`)
      .then((r) => setPendingQuizSubmissions(r.data.submissions || []))
      .catch(() => setPendingQuizSubmissions([]));
  }, [user?.role]);

  const loadTrainings = async () => {
    try {
      const response = await axios.get(`${API_URL}/trainings`);
      const trainingsData = await Promise.all(
        response.data.trainings.map(async (training) => {
          const detailResponse = await axios.get(`${API_URL}/trainings/${training.id}`);
          return detailResponse.data.training;
        })
      );
      setTrainings(trainingsData);
      if (user?.role === 'admin') {
        try {
          const pr = await axios.get(`${API_URL}/quizzes/submissions/pending`);
          setPendingQuizSubmissions(pr.data.submissions || []);
        } catch (e) {
          console.error('Error loading pending quiz submissions:', e);
        }
      }
    } catch (error) {
      console.error('Error loading trainings:', error);
      setToast({ message: 'Error loading trainings', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openGradeModal = async (submissionId) => {
    try {
      const res = await axios.get(`${API_URL}/quizzes/submissions/${submissionId}`);
      const writeIns = (res.data.questions || []).filter((q) => q.question_type === 'write_in');
      const initial = {};
      writeIns.forEach((q) => {
        initial[q.id] = true;
      });
      setWriteInGrades(initial);
      setGradeBundle(res.data);
      setShowGradeModal(true);
    } catch (error) {
      console.error('Error loading submission:', error);
      setToast({ message: 'Could not load submission', type: 'error' });
    }
  };

  const closeGradeModal = () => {
    setShowGradeModal(false);
    setGradeBundle(null);
    setWriteInGrades({});
  };

  const submitGrade = async (e) => {
    e.preventDefault();
    if (!gradeBundle?.submission?.id) return;
    const writeIns = (gradeBundle.questions || []).filter((q) => q.question_type === 'write_in');
    for (const q of writeIns) {
      if (writeInGrades[q.id] === undefined) {
        setToast({ message: 'Please mark each written response as correct or incorrect.', type: 'error' });
        return;
      }
    }
    setGradingSubmitting(true);
    try {
      await axios.post(`${API_URL}/quizzes/submissions/${gradeBundle.submission.id}/grade`, {
        write_in_scores: writeInGrades,
      });
      setToast({ message: 'Submission graded successfully', type: 'success' });
      closeGradeModal();
      loadTrainings();
    } catch (error) {
      console.error('Error grading:', error);
      setToast({
        message: error.response?.data?.error || 'Error saving grade',
        type: 'error',
      });
    } finally {
      setGradingSubmitting(false);
    }
  };

  const toggleTraining = (trainingId) => {
    const newExpanded = new Set(expandedTrainings);
    if (newExpanded.has(trainingId)) {
      newExpanded.delete(trainingId);
    } else {
      newExpanded.add(trainingId);
    }
    setExpandedTrainings(newExpanded);
  };

  const handleOpenTrainingModal = (training = null) => {
    if (training) {
      setEditingTraining(training);
      setTrainingFormData({
        title: training.title,
        description: training.description || '',
        order: Number.isFinite(Number(training.order)) ? Number(training.order) : 0,
      });
    } else {
      const maxOrder = trainings.length > 0
        ? Math.max(...trainings.map(t => Number.isFinite(Number(t.order)) ? Number(t.order) : -1))
        : -1;
      setEditingTraining(null);
      setTrainingFormData({
        title: '',
        description: '',
        order: maxOrder + 1,
      });
    }
    setShowTrainingModal(true);
  };

  const handleOpenModuleModal = (training, module = null) => {
    setSelectedTraining(training);
    if (module) {
      setEditingModule(module);
      setModuleFormData({
        title: module.title,
        description: module.description || '',
        type: module.type,
        content: module.content || '',
        video_url: module.video_url || '',
        file_url: module.file_url || '',
        video_source: module.video_source || 'embed',
        order: module.order,
      });
      // If editing a module with uploaded video, create a placeholder
      if (module.video_source === 'upload' && module.video_url) {
        const fileName = module.video_url.split('/').pop();
        setVideoFile({ name: fileName, size: 0, isExisting: true });
      } else {
        setVideoFile(null);
      }
      if (module.type === 'document' && module.file_url) {
        const fileName = module.file_name || module.file_url.split('/').pop();
        setDocumentFile({ name: fileName, size: 0, isExisting: true });
      } else {
        setDocumentFile(null);
      }
    } else {
      setEditingModule(null);
      const maxOrder = training.modules.length > 0 
        ? Math.max(...training.modules.map(m => m.order)) 
        : -1;
      setModuleFormData({
        title: '',
        description: '',
        type: 'text',
        content: '',
        video_url: '',
        file_url: '',
        video_source: 'embed',
        order: maxOrder + 1,
      });
      setVideoFile(null);
      setDocumentFile(null);
    }
    setShowModuleModal(true);
  };

  const handleOpenQuizModal = (training) => {
    setSelectedTraining(training);
    if (training.quiz) {
      setEditingQuiz(training.quiz);
      setQuizFormData({
        title: training.quiz.title || '',
        description: training.quiz.description || '',
        passing_score: training.quiz.passing_score || 70,
      });
    } else {
      setEditingQuiz(null);
      setQuizFormData({
        title: 'Quiz',
        description: '',
        passing_score: 70,
      });
    }
    setShowQuizModal(true);
  };

  const handleOpenQuestionModal = (quiz, question = null) => {
    setEditingQuiz(quiz);
    if (question) {
      setEditingQuestion(question);
      const qType = question.question_type === 'write_in' ? 'write_in' : 'multiple_choice';
      const opts = question.options?.length ? [...question.options] : ['', '', '', ''];
      setQuestionFormData({
        question: question.question,
        question_type: qType,
        options: opts.length >= 4 ? opts : [...opts, ...Array(4 - opts.length).fill('')].slice(0, 4),
        correct_answer:
          typeof question.correct_answer === 'number' ? question.correct_answer : 0,
        order: question.order,
      });
    } else {
      setEditingQuestion(null);
      const maxOrder = quiz.questions.length > 0
        ? Math.max(...quiz.questions.map(q => q.order))
        : -1;
      setQuestionFormData({
        question: '',
        question_type: 'multiple_choice',
        options: ['', '', '', ''],
        correct_answer: 0,
        order: maxOrder + 1,
      });
    }
    setShowQuestionModal(true);
  };

  const handleCloseModals = () => {
    setShowTrainingModal(false);
    setShowModuleModal(false);
    setShowQuizModal(false);
    setShowQuestionModal(false);
    setSelectedTraining(null);
    setEditingTraining(null);
    setEditingModule(null);
    setEditingQuiz(null);
    setEditingQuestion(null);
    setVideoFile(null);
    setDocumentFile(null);
  };

  const handleTrainingSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTraining) {
        await axios.put(`${API_URL}/trainings/${editingTraining.id}`, trainingFormData);
        setToast({ message: 'Training updated successfully', type: 'success' });
      } else {
        console.log('Creating training with data:', trainingFormData);
        const response = await axios.post(`${API_URL}/trainings`, trainingFormData);
        console.log('Training creation response:', response.data);
        setToast({ message: 'Training created successfully', type: 'success' });
      }
      handleCloseModals();
      loadTrainings();
    } catch (error) {
      console.error('Error saving training:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || 'Error saving training';
      setToast({ message: errorMessage, type: 'error' });
    }
  };

  const handleModuleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('training_id', selectedTraining.id);
      formData.append('title', moduleFormData.title);
      formData.append('description', moduleFormData.description || '');
      formData.append('type', moduleFormData.type);
      formData.append('order', moduleFormData.order);
      
      if (moduleFormData.type === 'text') {
        formData.append('content', moduleFormData.content);
      } else if (moduleFormData.type === 'video') {
        // For video type
        if (videoFile && !videoFile.isExisting) {
          // New file upload
          formData.append('video', videoFile);
          formData.append('video_source', 'upload');
        } else if (videoFile && videoFile.isExisting && editingModule) {
          // Existing uploaded video - keep the existing URL
          formData.append('video_url', moduleFormData.video_url);
          formData.append('video_source', 'upload');
        } else if (moduleFormData.video_url) {
          // YouTube URL
          formData.append('video_url', moduleFormData.video_url);
          formData.append('video_source', moduleFormData.video_source || 'embed');
        }
      } else if (moduleFormData.type === 'document') {
        if (documentFile && !documentFile.isExisting) {
          formData.append('document', documentFile);
        }
      }
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      
      if (editingModule) {
        await axios.put(`${API_URL}/training-modules/${editingModule.id}`, formData, config);
        setToast({ message: 'Module updated successfully', type: 'success' });
      } else {
        await axios.post(`${API_URL}/training-modules`, formData, config);
        setToast({ message: 'Module created successfully', type: 'success' });
      }
      handleCloseModals();
      loadTrainings();
    } catch (error) {
      console.error('Error saving module:', error);
      const msg = error.response?.data?.error || error.message || 'Error saving module';
      setToast({ message: msg, type: 'error' });
    }
  };

  const handleQuizSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/quizzes/training/${selectedTraining.id}`, quizFormData);
      setToast({ message: 'Quiz saved successfully', type: 'success' });
      handleCloseModals();
      loadTrainings();
    } catch (error) {
      setToast({ message: 'Error saving quiz', type: 'error' });
    }
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    try {
      const isWriteIn = questionFormData.question_type === 'write_in';
      const validOptions = questionFormData.options.filter((opt) => opt.trim() !== '');
      if (!isWriteIn && validOptions.length < 2) {
        setToast({ message: 'Multiple choice needs at least 2 options', type: 'error' });
        return;
      }

      const data = {
        question: questionFormData.question,
        question_type: isWriteIn ? 'write_in' : 'multiple_choice',
        order: questionFormData.order,
      };
      if (!isWriteIn) {
        data.options = validOptions;
        const ca = questionFormData.correct_answer;
        if (ca < 0 || ca >= validOptions.length) {
          setToast({ message: 'Select a valid correct answer', type: 'error' });
          return;
        }
        data.correct_answer = ca;
      }

      if (editingQuestion) {
        await axios.put(`${API_URL}/quizzes/questions/${editingQuestion.id}`, data);
        setToast({ message: 'Question updated successfully', type: 'success' });
      } else {
        await axios.post(`${API_URL}/quizzes/${editingQuiz.id}/questions`, data);
        setToast({ message: 'Question added successfully', type: 'success' });
      }
      handleCloseModals();
      loadTrainings();
    } catch (error) {
      setToast({ message: 'Error saving question', type: 'error' });
    }
  };

  const handleDeleteTraining = async (id) => {
    if (!window.confirm('Are you sure you want to delete this training? This will delete all modules and quizzes.')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/trainings/${id}`);
      setToast({ message: 'Training deleted successfully', type: 'success' });
      loadTrainings();
    } catch (error) {
      setToast({ message: 'Error deleting training', type: 'error' });
    }
  };

  const handleDeleteModule = async (id) => {
    if (!window.confirm('Are you sure you want to delete this module?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/training-modules/${id}`);
      setToast({ message: 'Module deleted successfully', type: 'success' });
      loadTrainings();
    } catch (error) {
      setToast({ message: 'Error deleting module', type: 'error' });
    }
  };

  const handleDeleteQuiz = async (trainingId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/quizzes/training/${trainingId}`);
      setToast({ message: 'Quiz deleted successfully', type: 'success' });
      loadTrainings();
    } catch (error) {
      setToast({ message: 'Error deleting quiz', type: 'error' });
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/quizzes/questions/${questionId}`);
      setToast({ message: 'Question deleted successfully', type: 'success' });
      loadTrainings();
    } catch (error) {
      setToast({ message: 'Error deleting question', type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#3c79bb] mb-4"></div>
          <div className="text-gray-900 font-semibold">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8">
      <header className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">Manage Trainings</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white/70 border border-white/50 rounded-xl p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#3c79bb] text-white' : 'text-gray-700 hover:bg-white/80'}`}
                aria-label="List view"
                title="List view"
              >
                <FiList className="text-base" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-[#3c79bb] text-white' : 'text-gray-700 hover:bg-white/80'}`}
                aria-label="Grid view"
                title="Grid view"
              >
                <FiGrid className="text-base" />
              </button>
            </div>
            <button
              onClick={() => handleOpenTrainingModal()}
              className="flex items-center gap-2 bg-[#3c79bb] text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl hover:bg-[#306196] transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 font-semibold"
            >
              <FiPlus className="text-lg" />
              <span className="hidden sm:inline">New Training</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {user?.role === 'admin' && pendingQuizSubmissions.length > 0 && (
          <div className="mb-6 glass-card rounded-2xl p-4 sm:p-5 border-2 border-amber-200 bg-amber-50/50">
            <h3 className="font-bold text-amber-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
              <FiClock className="text-lg flex-shrink-0" />
              Quiz responses awaiting grading ({pendingQuizSubmissions.length})
            </h3>
            <ul className="space-y-2">
              {pendingQuizSubmissions.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between flex-wrap gap-2 bg-white/80 rounded-xl p-3 border border-amber-100"
                >
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900">{s.user_name}</span>
                    <span className="text-gray-600 text-sm">
                      {' '}
                      — {s.training_title || s.quiz_title}
                    </span>
                    <span className="text-xs text-gray-500 block">
                      {new Date(s.submitted_at).toLocaleString()}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => openGradeModal(s.id)}
                    className="text-sm bg-[#3c79bb] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#306196] shrink-0"
                  >
                    Grade
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {trainings.length === 0 ? (
          <div className="glass-card rounded-2xl p-6 sm:p-8 lg:p-10 text-center">
            <FiBook className="text-5xl sm:text-6xl text-gray-400 mx-auto mb-4" />
            <p className="text-gray-900 mb-4 font-semibold text-lg">No trainings yet.</p>
            <button
              onClick={() => handleOpenTrainingModal()}
              className="bg-[#3c79bb] text-white px-6 py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 font-semibold"
            >
              Create your first training
            </button>
          </div>
        ) : (
          <div className={viewMode === 'list' ? 'space-y-4' : 'grid grid-cols-1 xl:grid-cols-2 gap-4'}>
            {trainings.map((training) => {
              const isExpanded = expandedTrainings.has(training.id);
              return (
                <div key={training.id} className="glass-card rounded-2xl overflow-hidden">
                  <div className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{training.title}</h3>
                        {training.description && (
                          <p className="text-gray-800 text-sm mb-2 font-medium">{training.description}</p>
                        )}
                        <div className="flex items-center flex-wrap gap-3 text-xs sm:text-sm text-gray-700 font-medium">
                          <span>{training.modules?.length || 0} Modules</span>
                          {training.quiz && <span className="flex items-center gap-1"><FiHelpCircle /> Quiz</span>}
                        </div>
                      </div>
                      <div className="flex items-start gap-2 shrink-0">
                        <button
                          onClick={() => toggleTraining(training.id)}
                          className="p-2 glass hover:glass-strong rounded-xl transition-all duration-200"
                        >
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </button>

                        <div className="flex flex-col items-center gap-1.5">
                          <button
                            onClick={() => handleOpenTrainingModal(training)}
                            className="p-2 glass text-[#3c79bb] hover:glass-strong rounded-xl transition-all duration-200"
                            title="Edit Training"
                          >
                            <FiEdit2 />
                          </button>
                        </div>

                        <button
                          onClick={() => handleDeleteTraining(training.id)}
                          className="p-2 glass text-red-600 hover:glass-strong rounded-xl transition-all duration-200"
                          title="Delete Training"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-6 space-y-4 pt-6 border-t border-white/20">
                        {/* Modules Section */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-bold text-gray-900">Modules</h4>
                            <button
                              onClick={() => handleOpenModuleModal(training)}
                              className="flex items-center gap-1 text-sm bg-[#3c79bb] text-white px-3 py-1.5 rounded-lg hover:bg-[#306196] transition-colors"
                            >
                              <FiPlus className="text-xs" />
                              Add Module
                            </button>
                          </div>
                          <div className="space-y-2">
                            {training.modules && training.modules.length > 0 ? (
                              training.modules
                                .sort((a, b) => a.order - b.order)
                                .map((module) => (
                                  <div key={module.id} className="flex items-center justify-between p-3 bg-white/50 rounded-xl">
                                    <div className="flex items-center gap-2 flex-1">
                                      {module.type === 'video' ? (
                                        <FiVideo className="text-[#3c79bb]" />
                                      ) : module.type === 'document' ? (
                                        <FiFile className="text-[#3c79bb]" />
                                      ) : (
                                        <FiFileText className="text-[#3c79bb]" />
                                      )}
                                      <span className="font-medium text-gray-900">{module.title}</span>
                                      <span className="text-xs text-gray-600">({module.type})</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleOpenModuleModal(training, module)}
                                        className="p-1.5 text-[#3c79bb] hover:bg-[#3c79bb]/10 rounded-lg transition-colors"
                                        title="Edit"
                                      >
                                        <FiEdit2 className="text-sm" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteModule(module.id)}
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Delete"
                                      >
                                        <FiTrash2 className="text-sm" />
                                      </button>
                                    </div>
                                  </div>
                                ))
                            ) : (
                              <p className="text-sm text-gray-600 italic">No modules yet</p>
                            )}
                          </div>
                        </div>

                        {/* Quiz Section */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-bold text-gray-900 flex items-center gap-2">
                              <FiHelpCircle /> Quiz
                            </h4>
                            {training.quiz ? (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleOpenQuizModal(training)}
                                  className="text-sm text-[#3c79bb] hover:underline font-medium"
                                >
                                  Edit Quiz
                                </button>
                                <button
                                  onClick={() => handleDeleteQuiz(training.id)}
                                  className="text-sm text-red-600 hover:underline font-medium"
                                >
                                  Delete
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleOpenQuizModal(training)}
                                className="flex items-center gap-1 text-sm bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors"
                              >
                                <FiPlus className="text-xs" />
                                Add Quiz
                              </button>
                            )}
                          </div>
                          {training.quiz && (
                            <div className="p-3 bg-white/50 rounded-xl">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-900">{training.quiz.title}</span>
                                <span className="text-xs text-gray-600">
                                  {training.quiz.questions?.length || 0} Questions
                                </span>
                              </div>
                              {training.quiz.questions && training.quiz.questions.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {training.quiz.questions
                                    .sort((a, b) => a.order - b.order)
                                    .map((question, idx) => (
                                      <div key={question.id} className="flex items-center justify-between p-2 bg-white rounded-lg gap-2">
                                        <span className="text-sm text-gray-700 min-w-0">
                                          <span
                                            className={`inline-block mr-2 px-2 py-0.5 rounded text-xs font-semibold ${
                                              question.question_type === 'write_in'
                                                ? 'bg-violet-100 text-violet-800'
                                                : 'bg-slate-100 text-slate-700'
                                            }`}
                                          >
                                            {question.question_type === 'write_in' ? 'Written' : 'MC'}
                                          </span>
                                          {idx + 1}. {question.question}
                                        </span>
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={() => handleOpenQuestionModal(training.quiz, question)}
                                            className="p-1 text-[#3c79bb] hover:bg-[#3c79bb]/10 rounded transition-colors"
                                            title="Edit"
                                          >
                                            <FiEdit2 className="text-xs" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteQuestion(question.id)}
                                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                            title="Delete"
                                          >
                                            <FiTrash2 className="text-xs" />
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                              <button
                                onClick={() => handleOpenQuestionModal(training.quiz)}
                                className="mt-3 text-sm text-[#3c79bb] hover:underline font-medium"
                              >
                                + Add Question
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Training Modal */}
      {showTrainingModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn"
          onClick={handleCloseModals}
        >
          <div
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp border border-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-white/20 flex items-center justify-between">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {editingTraining ? 'Edit Training' : 'Create Training'}
              </h2>
              <button
                onClick={handleCloseModals}
                className="p-2 glass hover:glass-strong rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900"
                aria-label="Close"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleTrainingSubmit} className="p-5 sm:p-6 space-y-4 sm:space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Title *</label>
                <input
                  type="text"
                  value={trainingFormData.title}
                  onChange={(e) => setTrainingFormData({ ...trainingFormData, title: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Description</label>
                <textarea
                  value={trainingFormData.description}
                  onChange={(e) => setTrainingFormData({ ...trainingFormData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Order</label>
                <input
                  type="number"
                  min="0"
                  value={trainingFormData.order}
                  onChange={(e) => setTrainingFormData({ ...trainingFormData, order: parseInt(e.target.value, 10) || 0 })}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#3c79bb] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 text-sm sm:text-base"
                >
                  {editingTraining ? 'Update Training' : 'Create Training'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModals}
                  className="flex-1 bg-gray-100 text-gray-900 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold border border-gray-300 text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Module Modal */}
      {showModuleModal && selectedTraining && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn"
          onClick={handleCloseModals}
        >
          <div
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slideUp border border-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-white/20 flex items-center justify-between">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {editingModule ? 'Edit Module' : 'Add Module'}
              </h2>
              <button
                onClick={handleCloseModals}
                className="p-2 glass hover:glass-strong rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900"
                aria-label="Close"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleModuleSubmit} className="p-5 sm:p-6 space-y-4 sm:space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Title *</label>
                <input
                  type="text"
                  value={moduleFormData.title}
                  onChange={(e) => setModuleFormData({ ...moduleFormData, title: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Description</label>
                <input
                  type="text"
                  value={moduleFormData.description}
                  onChange={(e) => setModuleFormData({ ...moduleFormData, description: e.target.value })}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Type *</label>
                <select
                  value={moduleFormData.type}
                  onChange={(e) => {
                    setModuleFormData({ ...moduleFormData, type: e.target.value, content: '', video_url: '', file_url: '', video_source: 'embed' });
                    setVideoFile(null);
                    setDocumentFile(null);
                    // Reset file input if it exists
                    const fileInput = document.querySelector('input[type="file"][accept="video/*"]');
                    if (fileInput) fileInput.value = '';
                    const documentInput = document.querySelector('input[type="file"][accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"]');
                    if (documentInput) documentInput.value = '';
                  }}
                  required
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium"
                >
                  <option value="text">Text</option>
                  <option value="video">Video</option>
                  <option value="document">PDF / Word File</option>
                </select>
              </div>
              {moduleFormData.type === 'text' ? (
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Content *</label>
                  <textarea
                    value={moduleFormData.content}
                    onChange={(e) => setModuleFormData({ ...moduleFormData, content: e.target.value })}
                    required
                    rows={12}
                    placeholder="Enter the training content here..."
                    className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                  />
                </div>
              ) : moduleFormData.type === 'video' ? (
                <div className="space-y-4">
                  {/* Video Source Selection */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">Video Source *</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="videoSource"
                          value="url"
                          checked={moduleFormData.video_source === 'embed'}
                          onChange={() => {
                            setVideoFile(null);
                            setModuleFormData({ ...moduleFormData, video_source: 'embed', video_url: '' });
                            // Reset file input
                            const fileInput = document.querySelector('input[type="file"][accept="video/*"]');
                            if (fileInput) fileInput.value = '';
                          }}
                          className="w-4 h-4 text-[#3c79bb] focus:ring-[#3c79bb]"
                        />
                        <span className="text-sm font-medium text-gray-900">YouTube URL</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="videoSource"
                          value="upload"
                          checked={moduleFormData.video_source === 'upload'}
                          onChange={() => {
                            setVideoFile(null);
                            setModuleFormData({ ...moduleFormData, video_source: 'upload', video_url: '' });
                          }}
                          className="w-4 h-4 text-[#3c79bb] focus:ring-[#3c79bb]"
                        />
                        <span className="text-sm font-medium text-gray-900">Upload Video File</span>
                      </label>
                    </div>
                  </div>

                  {/* YouTube URL Input */}
                  {moduleFormData.video_source === 'embed' && (
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">YouTube URL *</label>
                      <input
                        type="url"
                        value={moduleFormData.video_url}
                        onChange={(e) => setModuleFormData({ ...moduleFormData, video_url: e.target.value })}
                        required={moduleFormData.video_source === 'embed'}
                        placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                        className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                      />
                      <p className="text-xs text-gray-600 mt-1">Paste any YouTube URL (watch, short, or embed format)</p>
                    </div>
                  )}

                  {/* Video File Upload */}
                  {moduleFormData.video_source === 'upload' && (
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">Upload Video File *</label>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Check file size (500MB limit)
                            if (file.size > 500 * 1024 * 1024) {
                              setToast({ message: 'Video file must be less than 500MB', type: 'error' });
                              e.target.value = '';
                              return;
                            }
                            setVideoFile(file);
                            setModuleFormData({ ...moduleFormData, video_url: '' });
                          }
                        }}
                        required={moduleFormData.video_source === 'upload' && !moduleFormData.video_url}
                        className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#3c79bb] file:text-white hover:file:bg-[#306196]"
                      />
                      <p className="text-xs text-gray-600 mt-1">Supported formats: MP4, WebM, OGG, MOV, AVI, MKV (Max 500MB)</p>
                      {videoFile && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-800 font-medium">
                            Selected: {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Show selected file info */}
                  {videoFile && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-900">{videoFile.name}</p>
                          {videoFile.isExisting ? (
                            <p className="text-xs text-green-700">Existing uploaded video</p>
                          ) : (
                            <p className="text-xs text-green-700">{(videoFile.size / 1024 / 1024).toFixed(2)} MB</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setVideoFile(null);
                            setModuleFormData({ ...moduleFormData, video_source: 'embed' });
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">Upload PDF or Word File *</label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // 50MB client-side cap for docs (server allows more)
                          if (file.size > 50 * 1024 * 1024) {
                            setToast({ message: 'File must be less than 50MB', type: 'error' });
                            e.target.value = '';
                            return;
                          }
                          setDocumentFile(file);
                          setModuleFormData({ ...moduleFormData, file_url: '' });
                        }
                      }}
                      required={!editingModule || (!documentFile && !moduleFormData.file_url)}
                      className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#3c79bb] file:text-white hover:file:bg-[#306196]"
                    />
                    <p className="text-xs text-gray-600 mt-1">Supported formats: PDF, DOC, DOCX</p>
                    {documentFile && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800 font-medium">
                          Selected: {documentFile.name} {documentFile.isExisting ? '' : `(${(documentFile.size / 1024 / 1024).toFixed(2)} MB)`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Order</label>
                <input
                  type="number"
                  value={moduleFormData.order}
                  onChange={(e) => setModuleFormData({ ...moduleFormData, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#3c79bb] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 text-sm sm:text-base"
                >
                  {editingModule ? 'Update Module' : 'Create Module'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModals}
                  className="flex-1 bg-gray-100 text-gray-900 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold border border-gray-300 text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quiz Modal */}
      {showQuizModal && selectedTraining && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn"
          onClick={handleCloseModals}
        >
          <div
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp border border-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-white/20 flex items-center justify-between">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {editingQuiz ? 'Edit Quiz' : 'Add Quiz'}
              </h2>
              <button
                onClick={handleCloseModals}
                className="p-2 glass hover:glass-strong rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900"
                aria-label="Close"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleQuizSubmit} className="p-5 sm:p-6 space-y-4 sm:space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Title</label>
                <input
                  type="text"
                  value={quizFormData.title}
                  onChange={(e) => setQuizFormData({ ...quizFormData, title: e.target.value })}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Description</label>
                <textarea
                  value={quizFormData.description}
                  onChange={(e) => setQuizFormData({ ...quizFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Passing Score (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={quizFormData.passing_score}
                  onChange={(e) => setQuizFormData({ ...quizFormData, passing_score: parseInt(e.target.value) || 70 })}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#3c79bb] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 text-sm sm:text-base"
                >
                  {editingQuiz ? 'Update Quiz' : 'Create Quiz'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModals}
                  className="flex-1 bg-gray-100 text-gray-900 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold border border-gray-300 text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Question Modal */}
      {showQuestionModal && editingQuiz && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50 animate-fadeIn"
          onClick={handleCloseModals}
        >
          <div
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp border border-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-white/20 flex items-center justify-between">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {editingQuestion ? 'Edit Question' : 'Add Question'}
              </h2>
              <button
                onClick={handleCloseModals}
                className="p-2 glass hover:glass-strong rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900"
                aria-label="Close"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleQuestionSubmit} className="p-5 sm:p-6 space-y-4 sm:space-y-5">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Question *</label>
                <input
                  type="text"
                  value={questionFormData.question}
                  onChange={(e) => setQuestionFormData({ ...questionFormData, question: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <fieldset className="space-y-2">
                <legend className="block text-sm font-bold text-gray-900 mb-2">Answer type</legend>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-gray-900 font-medium">
                    <input
                      type="radio"
                      name="q_type"
                      checked={questionFormData.question_type === 'multiple_choice'}
                      onChange={() =>
                        setQuestionFormData({
                          ...questionFormData,
                          question_type: 'multiple_choice',
                          options:
                            questionFormData.options.filter(Boolean).length >= 2
                              ? questionFormData.options
                              : ['', '', '', ''],
                        })
                      }
                      className="w-4 h-4 text-[#3c79bb]"
                    />
                    Multiple choice (auto-graded)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-900 font-medium">
                    <input
                      type="radio"
                      name="q_type"
                      checked={questionFormData.question_type === 'write_in'}
                      onChange={() =>
                        setQuestionFormData({
                          ...questionFormData,
                          question_type: 'write_in',
                        })
                      }
                      className="w-4 h-4 text-[#3c79bb]"
                    />
                    Written response (graded later)
                  </label>
                </div>
                <p className="text-xs text-gray-600">
                  Written responses are submitted as text; an admin scores them from Manage Trainings.
                </p>
              </fieldset>
              {questionFormData.question_type === 'multiple_choice' && (
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Options *</label>
                  {questionFormData.options.map((option, index) => (
                    <div key={index} className="mb-2 flex items-center gap-2">
                      <input
                        type="radio"
                        name="correct_answer"
                        checked={questionFormData.correct_answer === index}
                        onChange={() =>
                          setQuestionFormData({ ...questionFormData, correct_answer: index })
                        }
                        className="w-4 h-4 text-[#3c79bb]"
                      />
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...questionFormData.options];
                          newOptions[index] = e.target.value;
                          setQuestionFormData({ ...questionFormData, options: newOptions });
                        }}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1 px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                      />
                    </div>
                  ))}
                  <p className="text-xs text-gray-600 mt-2">Select the radio button for the correct answer</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">Order</label>
                <input
                  type="number"
                  value={questionFormData.order}
                  onChange={(e) => setQuestionFormData({ ...questionFormData, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 sm:py-3 bg-white/80 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#3c79bb] focus:border-[#3c79bb] focus:bg-white transition-all text-gray-900 font-medium placeholder-gray-500"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-[#3c79bb] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-[#306196] transition-all duration-200 font-semibold shadow-lg shadow-[#3c79bb]/30 hover:shadow-xl hover:shadow-[#3c79bb]/40 text-sm sm:text-base"
                >
                  {editingQuestion ? 'Update Question' : 'Add Question'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModals}
                  className="flex-1 bg-gray-100 text-gray-900 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold border border-gray-300 text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showGradeModal && gradeBundle && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[60] animate-fadeIn"
          onClick={closeGradeModal}
        >
          <div
            className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp border border-white/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 sm:p-6 border-b border-gray-200 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Grade written responses</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {gradeBundle.user_name} · {gradeBundle.quiz?.title}
                </p>
                {gradeBundle.submission?.mc_total > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-graded multiple choice: {gradeBundle.submission.mc_correct} /{' '}
                    {gradeBundle.submission.mc_total} correct
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={closeGradeModal}
                className="p-2 glass hover:glass-strong rounded-xl transition-all text-gray-700"
                aria-label="Close"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <form onSubmit={submitGrade} className="p-5 sm:p-6 space-y-4">
              {(gradeBundle.questions || [])
                .filter((q) => q.question_type === 'write_in')
                .map((q) => {
                  const ans =
                    gradeBundle.submission?.answers?.[q.id] ??
                    gradeBundle.submission?.answers?.[String(q.id)] ??
                    '';
                  return (
                    <div key={q.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
                      <p className="font-semibold text-gray-900 mb-2">{q.question}</p>
                      <div className="text-gray-800 text-sm whitespace-pre-wrap bg-white border border-gray-200 rounded-lg p-3 mb-3 min-h-[3rem]">
                        {ans || '(No text)'}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setWriteInGrades({ ...writeInGrades, [q.id]: true })}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
                            writeInGrades[q.id] === true
                              ? 'border-green-600 bg-green-50 text-green-900'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-green-300'
                          }`}
                        >
                          Count as correct
                        </button>
                        <button
                          type="button"
                          onClick={() => setWriteInGrades({ ...writeInGrades, [q.id]: false })}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${
                            writeInGrades[q.id] === false
                              ? 'border-red-600 bg-red-50 text-red-900'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-red-300'
                          }`}
                        >
                          Count as incorrect
                        </button>
                      </div>
                    </div>
                  );
                })}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="submit"
                  disabled={gradingSubmitting}
                  className="flex-1 bg-[#3c79bb] text-white px-4 py-3 rounded-xl font-semibold hover:bg-[#306196] disabled:opacity-50"
                >
                  {gradingSubmitting ? 'Saving…' : 'Save grade'}
                </button>
                <button
                  type="button"
                  onClick={closeGradeModal}
                  className="flex-1 bg-gray-100 text-gray-900 px-4 py-3 rounded-xl font-semibold border border-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTraining;
